import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Scales, ShieldCheck, Handshake, Car, ArrowRight } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6, ease: 'easeOut' },
};

const Areas = () => {
  const areas = [
    {
      icon: Scales,
      title: 'Direito Trabalhista',
      description:
        'Análise e condução de demandas relacionadas à relação de trabalho, com atenção a verbas rescisórias, horas extras, vínculo de emprego, rescisão indireta, justa causa, assédio moral e demais irregularidades contratuais.',
      link: '/trabalhista',
      testId: 'area-trabalhista',
    },
    {
      icon: ShieldCheck,
      title: 'Plano de Saúde',
      description:
        'Atuação em situações de negativa de cobertura assistencial, incluindo cirurgia, exame, medicamento, internação, tratamento e demais medidas necessárias para análise da recusa e definição do caminho jurídico cabível.',
      link: '/plano-saude',
      testId: 'area-plano-saude',
    },
    {
      icon: Handshake,
      title: 'BPC/LOAS',
      description:
        'Orientação e atuação em questões relacionadas ao benefício assistencial, com foco em pedidos, negativas, suspensões, recursos e medidas cabíveis, conforme a situação concreta e os requisitos aplicáveis.',
      link: '/bpc-loas',
      testId: 'area-bpc-loas',
    },
    {
      icon: Car,
      title: 'Defesa em Busca e Apreensão',
      description:
        'Defesa em ações de busca e apreensão de veículos e bens, com análise criteriosa da legalidade da medida, revisão contratual e defesa dos direitos do cliente em processos judiciais.',
      link: '/busca-apreensao',
      testId: 'area-busca-apreensao',
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="py-24 bg-white" data-testid="areas-hero-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeUp} className="text-center max-w-4xl mx-auto">
            <h1 className="text-4xl sm:text-5xl font-bold text-[#2A2A2A] playfair tracking-tight mb-6">
              Áreas de Atuação
            </h1>
            <p className="text-xl text-[#737373] leading-relaxed">
              Atuação jurídica focada em demandas trabalhistas, de saúde, previdenciárias e defesa em busca e apreensão, com leitura estratégica
              do caso concreto e comunicação responsável com o cliente.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Areas Section */}
      <section className="py-24 bg-[#FAFAFA]" data-testid="areas-list-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeUp} className="text-center mb-16">
            <p className="text-lg text-[#737373] max-w-3xl mx-auto leading-relaxed">
              A RDC Advogados concentra sua atuação em áreas nas quais o cliente geralmente enfrenta decisões
              urgentes, impactos relevantes e necessidade de orientação técnica segura. Por isso, o escritório
              estrutura sua atuação em frentes específicas, com abordagem cuidadosa e juridicamente consistente.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {areas.map((area, index) => (
              <motion.div
                key={area.testId}
                {...fadeUp}
                transition={{ delay: index * 0.1, duration: 0.6 }}
                className="bg-white border border-[#2A2A2A]/10 rounded-sm p-8 hover:-translate-y-1 transition-transform duration-300 shadow-sm"
                data-testid={area.testId}
              >
                <area.icon size={48} weight="duotone" className="text-[#2A2A2A] mb-6" />
                <h2 className="text-2xl font-bold text-[#2A2A2A] playfair mb-4">{area.title}</h2>
                <p className="text-[#737373] leading-relaxed mb-6">{area.description}</p>
                <Link to={area.link}>
                  <Button
                    variant="outline"
                    data-testid={`${area.testId}-button`}
                    className="border-[#2A2A2A]/20 text-[#2A2A2A] hover:bg-[#FAFAFA] rounded-sm group"
                  >
                    Ver atuação
                    <ArrowRight
                      size={18}
                      weight="bold"
                      className="ml-2 group-hover:translate-x-1 transition-transform"
                    />
                  </Button>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-[#2A2A2A] text-white" data-testid="areas-cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div {...fadeUp}>
            <h2 className="text-3xl sm:text-4xl font-bold playfair mb-6">
              Orientação jurídica com direção clara e postura institucional sólida
            </h2>
            <p className="text-lg text-white/80 mb-8">
              Apresente sua situação à RDC Advogados e receba uma análise inicial com seriedade, critério e atenção
              ao contexto do seu caso.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contato">
                <Button
                  size="lg"
                  data-testid="areas-contact-button"
                  className="bg-white text-[#2A2A2A] hover:bg-white/90 rounded-sm font-medium px-8 py-6 text-base"
                >
                  Solicitar atendimento
                </Button>
              </Link>
              <a href="https://wa.me/552128880258" target="_blank" rel="noopener noreferrer">
                <Button
                  size="lg"
                  
                  data-testid="areas-whatsapp-button"
                  className="bg-[#25D366] text-white hover:bg-[#20BA5A] border-0 rounded-sm font-medium px-8 py-6 text-base"
                >
                  Falar pelo WhatsApp
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Areas;